import React, { useEffect, useState } from 'react';
import {
    ANALYTIC,
    COUNT,
    DATA_CONFIGURATION,
    DECIMAL_POINTS,
    DEFAULT_VALUE,
    ENTER,
    FIELD_CLASS,
    INCREMENT,
    LABEL,
    MANAGE,
    MAX,
    MAX_COUNT,
    MAX_QUANTITY,
    MIN,
    MIN_COUNT,
    MIN_QUANTITY,
    QUANTITY,
    REQUIRED
} from '../ISRConstants';
import {
    TConnector,
    TField,
    TFieldClass,
    TFieldDataConfiguration, TIndexState,
    TProductCategory,
    TSubmodel,
    TUpsertISRProjectDataModelsInput
} from '../Pages/DefineModel/model';
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton';
import { ClassIcon, GearIcon } from 'assests/GmsIcons';
import ManageClassDialog from '../Pages/DefineModel/Components/ManageClassDialog';
import { useISRContext } from '../Hooks/useISRContext';
import { getAllProjectClass } from '../Pages/DefineModel/ModelServices/ModalServices';
import { toast } from 'react-toastify';
import ConfigurationFilterComponent from './ConfigurationFilterComponent';

type TDecimalConfigurationProps = {
    id: string;
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    updateFieldDataConfiguration: (updatedConfig: TFieldDataConfiguration) => void;
    projectId?: string | null | undefined;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    indexInfo: TIndexState;
}

const DecimalConfiguration = ({id, selectedItem, updateFieldDataConfiguration, projectId, userProjectDataModels, indexInfo}: TDecimalConfigurationProps) => {
    const isrContext = useISRContext();
    const [fieldDataConfiguration, setFieldDataConfiguration] = useState<TFieldDataConfiguration>({});
    const [showClassMenu, setShowClassMenu] = useState<boolean>(false);

    const openConnectorMenu = () => setShowClassMenu(true);

    useEffect(() => {
        const existingValue = selectedItem && 'dataConfiguration' in selectedItem && selectedItem?.dataConfiguration ? selectedItem.dataConfiguration : {}
        setFieldDataConfiguration(existingValue);
    }, [selectedItem]);

    useEffect(() => {
        const hasChanges = Object.keys(fieldDataConfiguration).some(
            (key) => fieldDataConfiguration[key as keyof TFieldDataConfiguration] !== undefined
        );
        if (hasChanges) {
            updateFieldDataConfiguration(fieldDataConfiguration);
        }
    }, [fieldDataConfiguration]);

    const handleMinChange = (value: number) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, min: value}));
    };
    
    const handleMaxChange = (value: number) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, max: value}));
    };

    const handleIncrementChange = (value: number) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, increment: value}));
    };

    const handleDecimalPointsChange = (value: number) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, decimalPoints: value}));
    };

    const handleLabelChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, label: value}));
    };

    const handleDefaultValueChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, defaultValue: value.length > 0 ? Number(value): value}));
    };

    const handleRequiredChange = (value: boolean) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, fieldRequired: value}));
    }

    const handleAnalyticChange = (value: boolean) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, enableAnalytic: value}));
    }

    const handleClassChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = event.target.value;
        const selectedObject = isrContext?.fieldClasses?.find((item: TFieldClass) => item.name === selectedValue);
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, class: selectedObject}));
    };

    useEffect(() => {
        fetchAllClasses();
    }, []);

    const fetchAllClasses = async () => {
        try {
            const responseData = await getAllProjectClass(projectId ?? '');
            if (responseData?.done) {
                isrContext?.setFieldClasses(responseData?.data?.fieldClasses ?? []);
                isrContext?.setModelClasses(responseData?.data?.modelClasses ?? []);
            } else {
                toast.error(responseData?.message);
            }
        } catch (error) {
            console.log('Error', error);
        }
    }

    const handleMinMaxFields = (value: number, property: string) =>{
        setFieldDataConfiguration((prevConfig) => {
            if(!prevConfig) return prevConfig
            const updatedData = {
                ...prevConfig,
                [property]: value
            }
            return updatedData
        })
    }

    return (
        <React.Fragment>
            <div id={`${id}-decimal-config`} className='cart-style-dm'>
                <label id={`${id}-title`} className='cart-title'>{DATA_CONFIGURATION}</label>
                <div id={`${id}-main-section`} className='tw-flex tw-items-center tw-mt-3'>
                    <div id={`${id}-min-section`} className='tw-w-[15%]'>
                        <label id={`${id}-min-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MIN}</label>
                        <input 
                            id={`${id}-min-input`}
                            className="input-field-dm" 
                            placeholder={ENTER}
                            type='number'
                            step="any"
                            onChange={(e) => handleMinChange(Number(e.target.value))}
                            value={fieldDataConfiguration?.min ?? ''}
                        />
                    </div>
                    <div id={`${id}-max-section`} className='tw-w-[15%] tw-ms-5'>
                        <label id={`${id}-max-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MAX}</label>
                        <input 
                            id={`${id}-max-input`}
                            className="input-field-dm" 
                            placeholder={ENTER} 
                            type='number'
                            step='any'
                            onChange={(e) => handleMaxChange(Number(e.target.value))}
                            value={fieldDataConfiguration?.max}
                        />
                    </div>

                    <div id={`${id}-required-section`} className='tw-w-[10%] tw-ms-5'>
                        <label id={`${id}-required-label`} className='tw-text-sm tw-text-[#747593] tw-block tw-mb-2'>{REQUIRED}</label>
                        <ToggleButton
                            id={`${id}-required-toggle`}
                            name="Required" 
                            value={fieldDataConfiguration?.fieldRequired ?? false} 
                            onChange={(e) => handleRequiredChange(e.target.checked)} 
                        />
                    </div>
                    {/* <div id={`${id}-analytic-section`} className='tw-w-[10%] tw-ms-5'>
                        <div className='tw-flex tw-items-center tw-gap-1 tw-mb-1.5'>
                            <label id={`${id}-analytic-label`} className='tw-text-sm tw-text-[#747593] tw-block tw-mb-1'>{ANALYTIC}</label>
                        </div>
                        <ToggleButton
                            id={`${id}-analytic-toggle`}
                            name={ANALYTIC}
                            value={fieldDataConfiguration?.enableAnalytic ?? false}
                            onChange={(e) => handleAnalyticChange(e.target.checked)} 
                        />
                    </div> */}
                </div>

                <div id={`${id}-additional-section`} className='tw-flex tw-items-center tw-mt-3'>
                    <div id={`${id}-increment-section`} className='tw-w-[15%]'>
                        <label id={`${id}-increment-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{INCREMENT}</label>
                        <input 
                            id={`${id}-increment-input`}
                            className="input-field-dm" 
                            placeholder={ENTER} 
                            type='number'
                            step='any'
                            onChange={(e) => handleIncrementChange(Number(e.target.value))}
                            value={fieldDataConfiguration?.increment ?? ''}
                        />
                    </div>
                    <div id={`${id}-decimal-points-section`} className='tw-w-[15%] tw-ms-5'>
                        <label id={`${id}-decimal-points-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{DECIMAL_POINTS}</label>
                        <input 
                            id={`${id}-decimal-points-input`}
                            className="input-field-dm" 
                            placeholder={ENTER}
                            type='number'
                            step='any'
                            onChange={(e) => handleDecimalPointsChange(Number(e.target.value))}
                            value={fieldDataConfiguration?.decimalPoints ?? ''}
                        />
                    </div>
                    <div id={`${id}-label-section`} className='tw-w-[30%] tw-ms-5'>
                        <label id={`${id}-label-text`} className='tw-text-sm tw-text-[#747593] tw-block'>{LABEL}</label>
                        <input 
                            id={`${id}-label-input`}
                            className="input-field-dm" 
                            placeholder={ENTER} 
                            type='text'
                            onChange={(e) => handleLabelChange(e.target.value)}
                            value={fieldDataConfiguration?.label ?? ''}
                        />
                    </div>
                    <div id={`${id}-default-value-section`} className='tw-w-[15%] tw-ms-5'>
                        <label id={`${id}-default-value-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{DEFAULT_VALUE}</label>
                        <input 
                            id={`${id}-default-value-input`}
                            className="input-field-dm"
                            placeholder={ENTER}
                            type='number'
                            step='any'
                            onChange={(e) => handleDefaultValueChange(e.target.value)}
                            value={fieldDataConfiguration?.defaultValue ?? ''}
                        />
                    </div>
                </div>

                <ConfigurationFilterComponent 
                    id={`${id}-config-filter`}
                    fieldDataConfiguration={fieldDataConfiguration} 
                    setFieldDataConfiguration={setFieldDataConfiguration} 
                    userProjectDataModels={userProjectDataModels} 
                    indexInfo={indexInfo} 
                />

                <div id={`${id}-class-header`} className='tw-flex tw-items-center tw-gap-1 tw-mt-4'>
                    <ClassIcon />
                    <label id={`${id}-class-title`} className='cart-title'>{FIELD_CLASS}</label>
                </div>
                <div id={`${id}-class-section`} className='tw-flex tw-items-center tw-mt-1 tw-pb-3'>
                    <div id={`${id}-class-select-container`} className='tw-w-[30%] tw-pe-2'>
                        <select  
                            id={`${id}-class-select`}
                            className={`drop-down-dm`} 
                            value={fieldDataConfiguration?.class?.name} 
                            onChange={handleClassChange}
                        >
                            <option id={`${id}-class-default-option`}>Select Class</option>
                            {isrContext?.fieldClasses?.map((item: TFieldClass, index: number) => {
                                return (
                                    <option 
                                        id={`${id}-class-option-${index}`}
                                        value={item?.name} 
                                        key={item?.id ?? '' + index}
                                    >
                                        {item?.name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                    <button 
                        id={`${id}-manage-class-btn`}
                        onClick={openConnectorMenu} 
                        className='tw-w-auto tw-cursor-pointer tw-flex tw-items-center tw-gap-1 tw-ms-4'
                    >
                        <GearIcon />
                        <label id={`${id}-manage-label`} className='tw-text-sm tw-text-primary tw-block'>{MANAGE}</label>
                    </button>
                </div>

                <>
                    <label id={`${id}-count-title`} className='cart-title tw-mt-2'>{COUNT}</label>
                    <div id={`${id}-count-section`} className='tw-flex tw-items-center tw-mt-2'>
                        <div id={`${id}-count-min`} className='tw-w-[20%]'>
                            <label id={`${id}-count-min-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MIN}</label>
                            <input
                                id={`${id}-count-min-input`}
                                className="input-field-dm"
                                type='number'
                                value={fieldDataConfiguration?.minCount ?? 0}
                                onChange={(e) => handleMinMaxFields(Number(e.target.value), MIN_COUNT)}
                                placeholder={ENTER} 
                            />
                        </div>
                        <div id={`${id}-count-max`} className='tw-w-[20%] tw-ms-5'>
                            <label id={`${id}-count-max-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MAX}</label>
                            <input
                                id={`${id}-count-max-input`}
                                className="input-field-dm"
                                type='number'
                                value={fieldDataConfiguration?.maxCount ?? 0}
                                onChange={(e) => handleMinMaxFields(Number(e.target.value), MAX_COUNT)}
                                placeholder={ENTER} 
                            />
                        </div>
                    </div>
                    <label id={`${id}-quantity-title`} className='cart-title tw-mt-2'>{QUANTITY}</label>
                    <div id={`${id}-quantity-section`} className='tw-flex tw-items-center tw-mt-2'>
                        <div id={`${id}-quantity-min`} className='tw-w-[20%]'>
                            <label id={`${id}-quantity-min-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MIN}</label>
                            <input
                                id={`${id}-quantity-min-input`}
                                className="input-field-dm"
                                type='number'
                                value={fieldDataConfiguration?.minQuantity ?? 0}
                                onChange={(e) => handleMinMaxFields(Number(e.target.value), MIN_QUANTITY)}
                                placeholder={ENTER} 
                            />
                        </div>
                        <div id={`${id}-quantity-max`} className='tw-w-[20%] tw-ms-5'>
                            <label id={`${id}-quantity-max-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MAX}</label>
                            <input
                                id={`${id}-quantity-max-input`}
                                className="input-field-dm"
                                type='number'
                                value={fieldDataConfiguration?.maxQuantity ?? 0}
                                onChange={(e) => handleMinMaxFields(Number(e.target.value), MAX_QUANTITY)}
                                placeholder={ENTER} 
                            />
                        </div>
                    </div>
                </>
                {showClassMenu ? 
                    <ManageClassDialog 
                        id={`${id}-class-dialog`}
                        showClassMenu={showClassMenu} 
                        setShowClassMenu={setShowClassMenu} 
                        projectId={projectId}
                    /> 
                : null}
            </div>
        </React.Fragment>
    );
}

export default DecimalConfiguration;